import { memo, useState, useCallback } from "react";
import { useI18n } from "@/i18n";
import type { Translations, Locale } from "@/i18n";
import { useRouter } from "@/application/stores/routerStore";
import { colors, spacing, typography, transitions } from "@/design-system/tokens";

const NAV_LINKS: { key: string; href: string }[] = [
  { key: "features", href: "#features" },
  { key: "modules", href: "#modules" },
  { key: "pricing", href: "#pricing" },
  { key: "docs", href: "#docs" },
];
const LOCALES: Locale[] = ["es", "en"];

export const Navbar = memo(function Navbar() {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => setMenuOpen(v => !v), []);

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        display: "flex",
        justifyContent: "center",
        padding: `${spacing.md} ${spacing.xl}`,
        pointerEvents: "none",
      }}
    >
      <nav
        aria-label="Main navigation"
        style={{
          pointerEvents: "auto",
          display: "flex",
          alignItems: "center",
          gap: spacing.lg,
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: "9999px",
          padding: `10px 10px 10px 20px`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          width: "100%",
          maxWidth: "860px",
        }}
      >
        <Logo />

        <ul
          role="list"
          style={{
            display: "flex",
            alignItems: "center",
            gap: spacing.xs,
            listStyle: "none",
            flex: 1,
          }}
          className="nav-links"
        >
          {NAV_LINKS.map(link => (
            <NavItem key={link.key} label={t.nav[link.key as keyof typeof t.nav] ?? link.key} href={link.href} />
          ))}
        </ul>

        <div
          style={{ display: "flex", alignItems: "center", gap: spacing.sm }}
          className="nav-actions"
        >
          <LocaleSwitcher locale={locale} onSwitch={setLocale} />
          <LoginButton label={t.nav.login} onClick={() => navigate("login")} />
        </div>

        <MobileMenuButton open={menuOpen} onToggle={toggleMenu} />
      </nav>

      {menuOpen && <MobileMenu t={t} onClose={() => setMenuOpen(false)} />}
    </header>
  );
});

const Logo = memo(function Logo() {
  return (
    <a
      href="/"
      aria-label="Nexo-Warehouse home"
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing.sm,
        textDecoration: "none",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          background: colors.primary,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path
            d="M3 7L10 3L17 7V13L10 17L3 13V7Z"
            stroke="white"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M10 3V17M3 7L17 13M17 7L3 13"
            stroke="white"
            strokeWidth="1.1"
            strokeOpacity="0.55"
          />
        </svg>
      </div>
      <span
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.base,
          fontWeight: typography.weight.bold,
          color: "#111",
          letterSpacing: "-0.02em",
        }}
      >
        Nexo
      </span>
    </a>
  );
});

const NavItem = memo(function NavItem({ label, href }: { label: string; href: string }) {
  return (
    <li>
      <a
        href={href}
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.sm,
          fontWeight: typography.weight.medium,
          color: "#444",
          textDecoration: "none",
          padding: `${spacing.xs} ${spacing.sm}`,
          borderRadius: "6px",
          transition: `color ${transitions.fast}`,
          whiteSpace: "nowrap",
          display: "block",
        }}
        onMouseEnter={e =>
          ((e.currentTarget as HTMLAnchorElement).style.color = colors.primary)
        }
        onMouseLeave={e =>
          ((e.currentTarget as HTMLAnchorElement).style.color = "#444")
        }
      >
        {label}
      </a>
    </li>
  );
});

const LocaleSwitcher = memo(function LocaleSwitcher({
  locale,
  onSwitch,
}: {
  locale: Locale;
  onSwitch: (l: Locale) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Language selector"
      style={{
        display: "flex",
        background: "#f0f0f0",
        borderRadius: "9999px",
        padding: "3px",
        gap: "2px",
      }}
    >
      {LOCALES.map(l => (
        <button
          key={l}
          onClick={() => onSwitch(l)}
          aria-pressed={locale === l}
          aria-label={`Switch to ${l.toUpperCase()}`}
          style={{
            fontFamily: typography.fontFamily,
            fontSize: "11px",
            fontWeight: typography.weight.bold,
            padding: "3px 9px",
            borderRadius: "9999px",
            border: "none",
            cursor: "pointer",
            transition: `all ${transitions.fast}`,
            background: locale === l ? "#111" : "transparent",
            color: locale === l ? "#fff" : "#777",
            letterSpacing: "0.05em",
            lineHeight: 1.6,
          }}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
});

const LoginButton = memo(function LoginButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        fontWeight: typography.weight.bold,
        color: "#fff",
        background: "#111",
        padding: `${spacing.sm} ${spacing.lg}`,
        borderRadius: "9999px",
        border: "none",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: `background ${transitions.fast}`,
      }}
      onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = "#333")}
      onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = "#111")}
    >
      {label}
    </button>
  );
});

const NavTextLink = memo(function NavTextLink({
  label,
  onClick,
}: {
  label: string;
  onClick?: () => void;
}) {
  return (
    <a
      href="#"
      onClick={e => { e.preventDefault(); onClick?.(); }}
      style={{
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        fontWeight: typography.weight.medium,
        color: "#444",
        textDecoration: "none",
        whiteSpace: "nowrap",
        padding: `${spacing.xs} ${spacing.sm}`,
        transition: `color ${transitions.fast}`,
      }}
      onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.color = "#111")}
      onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.color = "#444")}
    >
      {label}
    </a>
  );
});

const ContactButton = memo(function ContactButton({ label }: { label: string }) {
  return (
    <a
      href="#contact"
      style={{
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        fontWeight: typography.weight.bold,
        color: "#fff",
        background: "#111",
        padding: `${spacing.sm} ${spacing.lg}`,
        borderRadius: "9999px",
        textDecoration: "none",
        whiteSpace: "nowrap",
        transition: `background ${transitions.fast}`,
        display: "inline-block",
      }}
      onMouseEnter={e =>
        ((e.currentTarget as HTMLAnchorElement).style.background = "#333")
      }
      onMouseLeave={e =>
        ((e.currentTarget as HTMLAnchorElement).style.background = "#111")
      }
    >
      {label}
    </a>
  );
});

const MobileMenuButton = memo(function MobileMenuButton({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      aria-label={open ? "Close menu" : "Open menu"}
      aria-expanded={open}
      onClick={onToggle}
      className="mobile-menu-btn"
      style={{
        display: "none",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: spacing.xs,
        color: "#333",
        borderRadius: "8px",
      }}
    >
      {open ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 12h18M3 6h18M3 18h18" />
        </svg>
      )}
    </button>
  );
});

const MobileMenu = memo(function MobileMenu({
  t,
  onClose,
}: {
  t: Translations;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-label="Mobile navigation"
      style={{
        position: "fixed",
        top: "80px",
        left: spacing.md,
        right: spacing.md,
        background: "rgba(255,255,255,0.97)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: "20px",
        padding: spacing.xl,
        display: "flex",
        flexDirection: "column",
        gap: spacing.sm,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        zIndex: 99,
      }}
    >
      {NAV_LINKS.map(link => (
        <a
          key={link.key}
          href={link.href}
          onClick={onClose}
          style={{
            fontFamily: typography.fontFamily,
            fontSize: typography.size.base,
            fontWeight: typography.weight.medium,
            color: "#333",
            textDecoration: "none",
            padding: `${spacing.sm} 0`,
            borderBottom: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          {t.nav[link.key as keyof typeof t.nav] ?? link.key}
        </a>
      ))}
      <a
        href="#contact"
        onClick={onClose}
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.base,
          fontWeight: typography.weight.bold,
          color: "#fff",
          background: "#111",
          padding: `${spacing.md} ${spacing.xl}`,
          borderRadius: "9999px",
          textDecoration: "none",
          textAlign: "center",
          marginTop: spacing.sm,
        }}
      >
        {t.nav.contact}
      </a>
    </div>
  );
});
